import React, { useState, useEffect } from 'react';
import CheckoutDrawer from './CheckoutDrawer';
import BuyerPicker from './BuyerPicker';
import ProductPicker from './ProductPicker';
import TenderTiles from './TenderTiles';
import ProductSalesAPI from '../../services/productSalesService';
import { HTTPService } from '../../services/httpService';
import { formatProductLabel, formatProductLine } from '../../util/productLabel';

const API = new HTTPService();

// NewSaleDrawer — the standalone register. CheckoutDrawer shell + BuyerPicker +
// a client-side product cart + TenderTiles. Commits in ONE call via
// createSale({ completeNow:true }): the server resolves tier pricing, deducts
// inventory, records payment, and (for card) builds the invoice.
//
// Pricing shown here is display-only; the server is authoritative on commit.
// Tax is client-supplied in v1 — pass `taxRate` (percent); dealers (wholesale)
// are tax-exempt. No deposits/partials on the register (pay-in-full).
//
// Props: show, onHide, installerId, locationId, createdBy, taxRate=0, onComplete?(sale)

const TEAL = '#3FB2BE';
const priceFor = (p, tier) => parseFloat(tier === 'wholesale' ? p.wholesale_price : p.retail_price) || 0;
const fmt = (n) => `$${(parseFloat(n) || 0).toFixed(2)}`;

// TenderTiles canonical method → product-sales payment_method + invoice flag.
const METHOD_MAP = {
  cash: { payment_method: 'cash', generateInvoice: false },
  check: { payment_method: 'check', generateInvoice: false },
  card: { payment_method: 'card', generateInvoice: true },     // Pay Online → hosted invoice
  terminal: { payment_method: 'card', generateInvoice: true }, // Tap/Swipe → hosted invoice (v1, no reader)
};

const NewSaleDrawer = ({ show, onHide, installerId, locationId, createdBy, taxRate = 0, onComplete }) => {
  const [buyer, setBuyer] = useState({ kind: 'walkin', tier: 'retail', id: null, name: null, isNew: true });
  const [cart, setCart] = useState([]); // [{ product, qty, lineDiscount }]
  const [saleDiscount, setSaleDiscount] = useState({ type: 'flat', value: 0 });
  const [showPicker, setShowPicker] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null); // completed sale
  const [taxRatePct, setTaxRatePct] = useState(parseFloat(taxRate) || 0);
  const [openJobs, setOpenJobs] = useState([]); // customer's open jobs (combine prompt)

  // Resolve the location's retail tax rate on open: location.stripe_tax_rate → /taxes/:id.
  useEffect(() => {
    let cancelled = false;
    if (!show || !installerId) return undefined;
    (async () => {
      try {
        const { data: locsRaw } = await API.get(`/installers/${installerId}/locations`);
        const locs = Array.isArray(locsRaw) ? locsRaw : (locsRaw?.data || locsRaw?.locations || []);
        const loc = (locationId && locationId !== 'all'
          ? locs.find((l) => String(l.location_id || l.Location_id) === String(locationId))
          : null) || locs[0];
        if (loc && loc.stripe_tax_rate) {
          const { data: t } = await API.get(`/taxes/${loc.stripe_tax_rate}`);
          if (!cancelled) setTaxRatePct(parseFloat(t.percentage) || 0);
        } else if (!cancelled) {
          setTaxRatePct(0);
        }
      } catch (err) {
        if (!cancelled) setTaxRatePct(0);
      }
    })();
    return () => { cancelled = true; };
  }, [show, installerId, locationId]);

  const tier = buyer?.tier || 'retail';

  const reset = () => {
    setCart([]); setSaleDiscount({ type: 'flat', value: 0 });
    setShowPicker(false); setProcessing(false); setError(null); setDone(null);
    setOpenJobs([]);
    setBuyer({ kind: 'walkin', tier: 'retail', id: null, name: null, isNew: true });
  };

  // Combine: add the cart to the customer's open job (Path A — products as
  // request_services custom lines on the job invoice; one payment at job checkout).
  const combineOntoJob = async (job) => {
    if (!cart.length) { setError('Add a product first.'); return; }
    setProcessing(true); setError(null);
    try {
      for (const c of cart) {
        const p = c.product;
        const nm = formatProductLine(p);
        await API.post('/requestService/custom', {
          description: nm,
          price: priceFor(p, 'retail'),
          quantity: c.qty,
          saveToCatalog: false,
          installerId,
          requestId: job.request_id,
          product_id: p.product_id || p.id,
        });
      }
      setDone({ combined: true, job });
      onComplete && onComplete({ combined: true, request_id: job.request_id });
    } catch (err) {
      setError('Could not add to the job. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const addToCart = (product) => {
    const id = product.product_id || product.id;
    setCart((prev) => {
      const i = prev.findIndex((c) => (c.product.product_id || c.product.id) === id);
      if (i >= 0) {
        const next = [...prev];
        next[i] = { ...next[i], qty: next[i].qty + 1 };
        return next;
      }
      return [...prev, { product, qty: 1, lineDiscount: 0 }];
    });
    setShowPicker(false);
  };
  const setQty = (id, qty) => setCart((prev) => prev.map((c) => ((c.product.product_id || c.product.id) === id ? { ...c, qty: Math.max(1, qty) } : c)));
  const setLineDiscount = (id, val) => setCart((prev) => prev.map((c) => ((c.product.product_id || c.product.id) === id ? { ...c, lineDiscount: val } : c)));
  const removeLine = (id) => setCart((prev) => prev.filter((c) => (c.product.product_id || c.product.id) !== id));

  // Display totals (server recomputes authoritatively on commit).
  const subtotal = cart.reduce((s, c) => s + priceFor(c.product, tier) * c.qty - (parseFloat(c.lineDiscount) || 0), 0);
  const saleDisc = saleDiscount.type === 'percent' ? subtotal * (parseFloat(saleDiscount.value) || 0) / 100 : (parseFloat(saleDiscount.value) || 0);
  const taxable = Math.max(0, subtotal - saleDisc);
  const tax = tier === 'wholesale' ? 0 : taxable * (parseFloat(taxRatePct) || 0) / 100;
  const total = taxable + tax;

  const buyerFields = () => {
    if (buyer?.kind === 'customer') return { customer_id: buyer.id };
    if (buyer?.kind === 'dealer') return { dealer_id: buyer.id };
    return { buyer_name: buyer?.name || 'Walk-in', buyer_email: buyer?.email || undefined, buyer_phone: buyer?.phone || undefined };
  };

  const collect = async ({ method }) => {
    if (!cart.length) { setError('Add a product first.'); return; }
    const tender = METHOD_MAP[method] || METHOD_MAP.cash;
    setProcessing(true); setError(null);
    try {
      const payload = {
        installer_id: installerId,
        location_id: locationId,
        ...buyerFields(),
        items: cart.map((c) => ({
          product_id: c.product.product_id || c.product.id,
          quantity: c.qty,
          line_discount: parseFloat(c.lineDiscount) || 0,
          inventory_item_id: null,
        })),
        discount_type: saleDiscount.type,
        discount_value: parseFloat(saleDiscount.value) || 0,
        tax_amount: Number(tax.toFixed(2)),
        sale_type: 'standalone',
        created_by: createdBy,
        completeNow: true,
        payment_method: tender.payment_method,
        generateInvoice: tender.generateInvoice,
      };
      const res = await ProductSalesAPI.createSale(payload);
      const sale = res.sale || res;
      // Card/online → surface the hosted invoice so the buyer can pay.
      if (tender.generateInvoice && sale.invoice_url) {
        window.open(sale.invoice_url, '_blank', 'noopener,noreferrer');
      }
      setDone(sale);
      onComplete && onComplete(sale);
    } catch (err) {
      setError(err?.message || 'Could not complete the sale. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const subtitle = buyer?.name ? `${buyer.name}${tier === 'wholesale' ? ' · Wholesale' : ''}` : 'Walk-in';

  return (
    <CheckoutDrawer
      show={show}
      onHide={() => { onHide && onHide(); }}
      title={done ? (done.combined ? 'Added to job' : 'Sale complete') : 'New Sale'}
      subtitle={done ? (done.combined ? (done.job?.label || undefined) : (done.sale_number || undefined)) : subtitle}
      footer={done ? (
        <button type="button" className="btn btn-block" style={{ background: TEAL, color: '#fff', borderRadius: 6, fontWeight: 600 }} onClick={reset}>
          <i className="fa-sharp fa-regular fa-plus mr-2" /> New sale
        </button>
      ) : null}
    >
      {done && done.combined ? (
        <div className="text-center py-3">
          <div style={{ color: '#28A745', fontSize: 40 }}><i className="fa-sharp fa-regular fa-circle-check" /></div>
          <div style={{ fontSize: 18, fontWeight: 700, marginTop: 8 }}>Added to job</div>
          <div className="text-muted" style={{ fontSize: 13, marginTop: 4 }}>{done.job.label} — collect at the job's checkout.</div>
        </div>
      ) : done ? (
        <div className="text-center py-3">
          <div style={{ color: '#28A745', fontSize: 40 }}><i className="fa-sharp fa-regular fa-circle-check" /></div>
          <div style={{ fontSize: 22, fontWeight: 700, marginTop: 8 }}>{fmt(done.total ?? total)}</div>
          <div className="text-muted" style={{ fontSize: 13, marginTop: 4 }}>
            {done.sale_number ? `${done.sale_number} · ` : ''}{(done.payments && done.payments[0]?.payment_state) || 'paid'}
          </div>
          {(done.items || []).length > 0 && (
            <div style={{ maxWidth: 300, margin: '14px auto 0', textAlign: 'left' }}>
              {done.items.map((it, i) => (
                <div key={i} className="d-flex justify-content-between" style={{ fontSize: 13, color: '#6B7280' }}>
                  <span>{(it.product_name || it.name)}{it.quantity > 1 ? ` ×${it.quantity}` : ''}</span>
                  <span>{fmt(it.line_total)}</span>
                </div>
              ))}
            </div>
          )}
          {done.invoice_url && (
            <a href={done.invoice_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-secondary mt-3">
              <i className="fa-sharp fa-regular fa-receipt mr-2" /> View invoice
            </a>
          )}
        </div>
      ) : showPicker ? (
        <ProductPicker
          installerId={installerId}
          locationId={locationId}
          tier={tier}
          onAdd={(product) => addToCart(product)}
          onClose={() => setShowPicker(false)}
        />
      ) : (
        <React.Fragment>
          <BuyerPicker
            installerId={installerId}
            locationId={locationId}
            onBuyerChange={(b) => {
              setBuyer(b || { kind: 'walkin', tier: 'retail', id: null, name: null, isNew: true });
              if (!b || b.kind !== 'customer') setOpenJobs([]);
            }}
            onExistingMatch={async (c) => {
              try {
                const params = new URLSearchParams({ customer_id: c.customer_id, installer_id: installerId });
                if (locationId && locationId !== 'all') params.set('location_id', locationId);
                const { data } = await API.get(`/api/requests/open?${params.toString()}`);
                setOpenJobs((data && data.jobs) || []);
              } catch (e) {
                setOpenJobs([]);
              }
            }}
          />

          {openJobs.length > 0 && (
            <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: '#ECF9FB', border: '1px solid #3FB2BE' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#14555B' }}>
                <i className="fa-sharp fa-regular fa-code-merge mr-2" />Open job found
              </div>
              <div style={{ fontSize: 13, color: '#236A72', marginTop: 4 }}>
                {openJobs[0].label}{openJobs[0].total != null ? ` · ${fmt(openJobs[0].total)}` : ''} — add these products to that invoice so they pay once?
              </div>
              <div className="d-flex" style={{ gap: 8, marginTop: 8 }}>
                <button type="button" className="btn btn-sm" style={{ background: TEAL, color: '#fff', borderRadius: 6, fontWeight: 600, flex: 1 }} disabled={processing || !cart.length} onClick={() => combineOntoJob(openJobs[0])}>
                  Add to that invoice
                </button>
                <button type="button" className="btn btn-sm btn-light" style={{ borderRadius: 6, flex: 1 }} onClick={() => setOpenJobs([])}>
                  Keep separate
                </button>
              </div>
            </div>
          )}

          <div className="mt-3">
            <div className="text-muted mb-2" style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.5px' }}>CART</div>
            {cart.length === 0 && <div className="text-muted" style={{ fontSize: 13 }}>No items yet.</div>}
            {cart.map((c) => {
              const id = c.product.product_id || c.product.id;
              const unit = priceFor(c.product, tier);
              return (
                <div key={id} className="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>{formatProductLabel(c.product)}</div>
                    <div className="d-flex align-items-center" style={{ fontSize: 12, color: '#9CA3AF', gap: 5 }}>
                      <span>{fmt(unit)} ea</span>
                      <span>· −$</span>
                      <input
                        type="number" min="0" step="0.01"
                        value={c.lineDiscount || ''}
                        onChange={(e) => setLineDiscount(id, e.target.value)}
                        placeholder="0"
                        className="form-control form-control-sm"
                        style={{ width: 54, height: 22, fontSize: 12, padding: '0 4px' }}
                      />
                    </div>
                  </div>
                  <div className="d-flex align-items-center" style={{ gap: 10 }}>
                    <div className="d-flex align-items-center" style={{ gap: 8, border: '1px solid #E5E7EB', borderRadius: 6, padding: '2px 6px' }}>
                      <i className="fa-sharp fa-regular fa-minus" style={{ cursor: 'pointer', color: '#6B7280', fontSize: 12 }} onClick={() => setQty(id, c.qty - 1)} />
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{c.qty}</span>
                      <i className="fa-sharp fa-regular fa-plus" style={{ cursor: 'pointer', color: '#6B7280', fontSize: 12 }} onClick={() => setQty(id, c.qty + 1)} />
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{fmt(unit * c.qty - (parseFloat(c.lineDiscount) || 0))}</span>
                    <i className="fa-sharp fa-regular fa-xmark" style={{ cursor: 'pointer', color: '#9CA3AF', fontSize: 14 }} onClick={() => removeLine(id)} />
                  </div>
                </div>
              );
            })}
            <button
              type="button"
              className="btn btn-block mt-2 d-flex align-items-center justify-content-center"
              style={{ border: '1px solid #E5E7EB', borderRadius: 6, color: TEAL, fontSize: 14, fontWeight: 500 }}
              onClick={() => setShowPicker(true)}
            >
              <i className="fa-sharp fa-regular fa-plus mr-2" /> Add product
            </button>
          </div>

          <div className="border-top pt-3 mt-3">
            <div className="d-flex justify-content-between mb-1" style={{ fontSize: 14 }}>
              <span className="text-muted">Subtotal</span><span>{fmt(subtotal)}</span>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: 14 }}>
              <span className="text-muted">Discount</span>
              <div className="d-flex align-items-center" style={{ gap: 6 }}>
                <div className="btn-group btn-group-sm" role="group">
                  <button type="button" className={`btn btn-sm ${saleDiscount.type === 'flat' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setSaleDiscount((d) => ({ ...d, type: 'flat' }))}>$</button>
                  <button type="button" className={`btn btn-sm ${saleDiscount.type === 'percent' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setSaleDiscount((d) => ({ ...d, type: 'percent' }))}>%</button>
                </div>
                <input
                  type="number" min="0" step="0.01"
                  className="form-control form-control-sm" style={{ width: 72 }}
                  value={saleDiscount.value || ''}
                  onChange={(e) => setSaleDiscount((d) => ({ ...d, value: e.target.value }))}
                  placeholder="0"
                />
                {saleDisc > 0 && <span className="text-success" style={{ minWidth: 56, textAlign: 'right' }}>-{fmt(saleDisc)}</span>}
              </div>
            </div>
            {tax > 0 && (
              <div className="d-flex justify-content-between mb-1" style={{ fontSize: 14 }}>
                <span className="text-muted">Tax</span><span>{fmt(tax)}</span>
              </div>
            )}
          </div>

          <div className="border-top pt-3 mt-3">
            <TenderTiles
              amountDue={total}
              processing={processing}
              error={error}
              allowPartial={false}
              onCollect={collect}
            />
          </div>
        </React.Fragment>
      )}
    </CheckoutDrawer>
  );
};

export default NewSaleDrawer;
